import React, {useEffect, useState, useRef} from 'react';
import {useTranslation} from "react-i18next";
import {Button, Checkbox, Form, Input, message, Modal, Select, Tabs, Upload } from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined, UploadOutlined, WarningOutlined, DownloadOutlined } from '@ant-design/icons';
import settings from "../services/settings.service";
import positionServer from "../services/position-server.service";

import './privateSettings.css';

const {Option} = Select;
const layout = {
    labelCol: {span: 8},
    wrapperCol: {span: 16},
};
const tailLayout = {
    wrapperCol: {offset: 8, span: 16},
};

/* -------------------------------------------------------------------------------- *\
|                               PanelHPv2Selection
\* -------------------------------------------------------------------------------- */
function PanelHPv2Selection({ hpv2List, selected, formHpv2Selection })
{
    const { t } = useTranslation();
    useEffect(() =>
    {
        formHpv2Selection.setFieldsValue({hpv2_uuid: `${selected.uuid}`});
        formHpv2Selection.setFieldsValue({is_hpv2_control_enabled: selected.isHpv2ControlEnabled});
    }, [formHpv2Selection, selected]);

    const onFinish = (values) =>
    {
        const positionServerUpdate =
        {
            uuid: values.hpv2_uuid,
            isHpv2ControlEnabled: values.is_hpv2_control_enabled,
            isSelected: true,
        };
        positionServer
            .update(positionServerUpdate)
            .then(res =>
            {
                if (res.status === 200 || res.status === 204)
                {
                    message.success(t('common.save_success'));
                    setTimeout(() => window.location.reload(), 2000);
                } else
                {
                    message.error(t('common.error'));
                }
            })
            .catch(() =>
            {
                message.error(t('common.error'));
            })
    }

    const hpv2GetLabel = (item) =>
    {
        return item.name + ' (' + item.url + ')';
    }

    return  <>
        <Form
            {...layout}
            form={formHpv2Selection}
            name="hpv2_selection_form"
            className="common_form"
            onFinish={onFinish}>

            <Form.Item name="hpv2_uuid" label={t('settings.hpv2_selection.hpv2_addr')} rules={[{required: true}]}>
                <Select onChange={(e) => formHpv2Selection.setFieldsValue({is_hpv2_control_enabled: hpv2List.find(hpv2 => hpv2.uuid === e).isHpv2ControlEnabled})}>
                    {hpv2List.map(hpv2 =>
                    {
                        return <Option key={hpv2.uuid} value={hpv2.uuid}>
                            {hpv2GetLabel(hpv2)}
                        </Option>
                    })}
                </Select>
            </Form.Item>

            <Form.Item name="is_hpv2_control_enabled" label={t('settings.hpv2_selection.hpv2_control_enabled')} valuePropName="checked">
                <Checkbox/>
            </Form.Item>

            <Form.Item {...tailLayout} className="form_toolbar">
                <Button type="primary" htmlType="submit">
                    {t('common.save')}
                </Button>
            </Form.Item>

        </Form>
    </>;
}

/* -------------------------------------------------------------------------------- *\
|                               PanelHPv2Configuration
\* -------------------------------------------------------------------------------- */
function PanelHPv2Configuration({formHpv2Settings}) {
    const {t} = useTranslation();

    const [mccMncList, setMccMncList] = useState([]);
    const [countries, setCountries] = useState([]);
    const [selectedCountries, setSelectedCountries] = useState([]);
    const [countriesModalOpen, setCountriesModalOpen] = useState(false);

    const loadMccMncList = () => {
        return positionServer.getAllMccMnc().then(res => {
            setMccMncList((res && res.data) || []);
        })
    }

    useEffect(() => {
        loadMccMncList();
    }, [])

    const showCountries = () => {
        Promise.all([positionServer.getCountriesMccMnc(), settings.getMccMncCountries()])
            .then(([countriesRes, selected]) => {
                setCountries((countriesRes && countriesRes.data) || []);
                setSelectedCountries(selected || []);
                setCountriesModalOpen(true);
            })
            .catch(() => message.error(t('common.error')));
    }

    const toggleCountry = (countryCode, checked) => {
        setSelectedCountries(prev => checked
            ? [...prev, countryCode]
            : prev.filter(code => code !== countryCode));
    }

    const saveCountries = () => {
        settings.saveMccMncCountries(selectedCountries)
            .then(() => {
                message.success(t('common.save_success'));
                setCountriesModalOpen(false);
                // refresh the MCC MNC select to reflect the newly selected countries
                return loadMccMncList();
            })
            .catch(() => message.error(t('common.error')));
    }

    const onFinish = (values) => {
        const settingsServerUpdate ={
            mode: Number(values.hpv2_mode),
            height_sensor: Number(values.hpv2_tracker_height),
            channel: Number(values.hpv2_channel),
            mccMnc: Number(values.hpv2_mcc_mnc),
            networkTechnology: Number(values.hpv2_network_technology)};
        positionServer
            .updateHpv2Settings(settingsServerUpdate)
            .then(res => {
                if (res.status === 200 || res.status === 204) {
                    message.success(t('common.save_success'));
                } else {
                    message.error(t('common.error'));
                }
            })
            .catch(() => {
                message.error(t('common.error'));
            })
    }

    return <>
        <Form
            {...layout}
            form={formHpv2Settings}
            name="hpv2_settings_form"
            className="common_form"
            onFinish={onFinish}>
            <fieldset>
                <legend>{t('settings.hpv2_settings.general_legend')}</legend>
                <Form.Item name="hpv2_mode" label={t('settings.hpv2_settings.hpv2_mode_label')} rules={[{required: true}]}>
                    <Select value="hpv2_mode">
                        <Option key="outdoor" value="1">
                            {t('settings.hpv2_settings.hpv2_mode_outdoor')}
                        </Option>
                        <Option key="indoor" value="2">
                            {t('settings.hpv2_settings.hpv2_mode_indoor')}
                        </Option>
                        <Option key="outdoor-fast" value="3">
                            {t('settings.hpv2_settings.hpv2_mode_outdoor_fast')}
                        </Option>
                        <Option key="outdoor-sync-gps" value="4">
                            {t('settings.hpv2_settings.hpv2_mode_outdoor_sync_gps')}
                        </Option>
                    </Select>
                </Form.Item>
                <Form.Item name="hpv2_tracker_height" label={t('settings.hpv2_settings.hpv2_tracker_height_label')} rules={[{required: true}]}>
                    <Input placeholder="" type="number"/>
                </Form.Item>
                <Form.Item name="hpv2_channel" label={t('settings.hpv2_settings.hpv2_channel_label')} rules={[{required: true}]}>
                    <Select value="hpv2_channel">
                        <Option key="hpv2_channel_1" value="1">
                            1
                        </Option>
                        <Option key="hpv2_channel_2" value="2">
                            2
                        </Option>
                        <Option key="hpv2_channel_3" value="3">
                            3
                        </Option>
                        <Option key="hpv2_channel_5" value="5">
                            5
                        </Option>
                    </Select>
                </Form.Item>
            </fieldset>
            <fieldset>
                <legend>{t('settings.hpv2_settings.network_legend')}</legend>
                <Form.Item {...tailLayout}>
                    <Button htmlType="button" onClick={showCountries}>
                        {t('settings.hpv2_settings.show_countries')}
                    </Button>
                </Form.Item>
                <Form.Item name="hpv2_mcc_mnc"
                        label={t('settings.hpv2_settings.hpv2_mcc_mnc_label')}
                        rules={[{required: true}]}
                        extra={t('settings.hpv2_settings.hpv2_mcc_mnc_extra')}>
                    <Select value="hpv2_mcc_mnc">
                        {mccMncList.map(mccMnc => {
                            return <Option key={`${mccMnc.mcc}${mccMnc.mnc}`} value={`${mccMnc.mcc}${mccMnc.mnc}`}>
                                {mccMnc.countryCode + ' ' + mccMnc.brand + ' '+ mccMnc.mcc+mccMnc.mnc}
                            </Option>
                        })}
                    </Select>
                </Form.Item>
                <Form.Item name="hpv2_network_technology" label={t('settings.hpv2_settings.hpv2_network_technology_label')} rules={[{required: true}]}>
                    <Select value="hpv2_network_technology">
                        <Option key="hpv2_network_technology_3" value="3">
                            3G
                        </Option>
                        <Option key="hpv2_network_technology_4" value="4">
                            4G
                        </Option>
                    </Select>
                </Form.Item>
            </fieldset>
            <Form.Item {...tailLayout} className="form_toolbar">
                <Button type="primary" htmlType="submit">
                    {t('common.save')}
                </Button>
            </Form.Item>
        </Form>

        <Modal
            title={t('settings.hpv2_settings.show_countries')}
            open={countriesModalOpen}
            okText={t('common.save')}
            onOk={saveCountries}
            onCancel={() => setCountriesModalOpen(false)}
            maskClosable={false}
        >
            <table className="mcc-mnc-countries-table" style={{ borderCollapse: 'collapse', width: '100%' }}>
                <thead>
                    <tr>
                        <th style={{ border: '1px solid #d9d9d9', padding: '4px 12px' }}></th>
                        <th style={{ border: '1px solid #d9d9d9', padding: '4px 12px', textAlign: 'left' }}>{t('settings.hpv2_settings.countries_modal_code')}</th>
                        <th style={{ border: '1px solid #d9d9d9', padding: '4px 12px', textAlign: 'left' }}>{t('settings.hpv2_settings.countries_modal_country')}</th>
                    </tr>
                </thead>
                <tbody>
                    {countries.map(country => (
                        <tr key={country.countryCode}>
                            <td style={{ border: '1px solid #d9d9d9', padding: '4px 12px', textAlign: 'center' }}>
                                <Checkbox
                                    checked={selectedCountries.includes(country.countryCode)}
                                    onChange={e => toggleCountry(country.countryCode, e.target.checked)}
                                />
                            </td>
                            <td style={{ border: '1px solid #d9d9d9', padding: '4px 12px' }}>{country.countryCode}</td>
                            <td style={{ border: '1px solid #d9d9d9', padding: '4px 12px' }}>{country.countryName}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </Modal>
    </>;
}
/* -------------------------------------------------------------------------------- *\
|                               PanelServerManager
\* -------------------------------------------------------------------------------- */
let g_indebug = false;
let g_inlogs = false;
function PanelServerManager()
{
    const {t} = useTranslation();
    const [debug, setDebug] = useState(false);
    const [logs, setLogs] = useState([]);
    const [inter, setInter] = useState(500);
    const [logtype, setLogtype] = useState("racing");
    const ref = useRef();

    useEffect(() =>
    {
        g_indebug = true;
        settings.getDebug().then((b) => { setDebug(b); g_indebug = false; });
        const interval = setInterval(() =>
        {
            if (g_indebug) return;
            g_indebug = true;
            settings.getDebug().then((b) => { setDebug(b); g_indebug = false; });
        }, 10000);
        return () => clearInterval(interval);
    }, []);
    useEffect(() =>
    {
        g_inlogs = true;
        settings.getLogs(logtype, 0)
            .then((l) =>
            {
                setLogs(l);
                setTimeout(() => { ref.current.scrollTop = ref.current.scrollHeight; }, 100);
            })
            .catch(function (error) { message.warning(error?.message); })
            .finally(() => { g_inlogs = false; });
    }, [logtype]);
    useEffect(() =>
    {
        const interval = setInterval(() =>
        {
            if (g_inlogs) return;
            g_inlogs = true;
            settings.getLogs(logtype, logs.length)
                .then((l) =>
                {
                    if (l.length > 0)
                    {
                        const isAtBottom = ref.current && (ref.current.clientHeight + ref.current.scrollTop) >= (ref.current.scrollHeight - 50);
                        setLogs([...logs, ...l]);

                        if (isAtBottom)
                        {
                            setTimeout(() => { ref.current.scrollTop = ref.current.scrollHeight; }, 100);
                        }
                        setInter(500);
                    }
                    else
                    {
                        setInter(5000);
                    }
                })
                .catch(function (error) { message.warning(error?.message); })
                .finally(() => { g_inlogs = false; });
        }, inter);
        return () => clearInterval(interval);
    // eslint-disable-next-line
    }, [logs, inter]);

    return <>
        <div className="server_manager">
            <fieldset className="server_manager_applications">
                <legend className="server_manager_title">{t('settings.server_manager.internal_title')}</legend>
                <div className="server_manager_application">
                    <div className="server_manager_label">{t('settings.server_manager.debug_label')}</div>
                    <div className="server_manager_debug">
                        <Button type="primary" size='small' htmlType="button" onClick={() => settings.setDebug(true).then(() => message.success(t('common.save_success')))}>{t('settings.server_manager.debug_active')}</Button>
                        <Button type="primary" size='small' htmlType="button" onClick={() => settings.setDebug(false).then(() => message.success(t('common.save_success')))}>{t('settings.server_manager.debug_inactive')}</Button>
                        <div className="server_manager_debug_value">{debug ? t('settings.server_manager.debug_active') : t('settings.server_manager.debug_inactive')}</div>
                    </div>
                </div>
                <legend className="server_manager_title">
                    <div>{t('settings.server_manager.logs_title')}</div>
                </legend>
                <div className="server_manager_logtype">
                    <div className={logtype === "racing" ? "active" : ""} onClick={() => setLogtype("racing")}>{t('settings.server_manager.logtype_racing')}</div>
                    <div className={logtype === "engine" ? "active" : ""} onClick={() => setLogtype("engine")}>{t('settings.server_manager.logtype_engine')}</div>
                    <div className={logtype === "mec" ? "active" : ""} onClick={() => setLogtype("mec")}>{t('settings.server_manager.logtype_mec')}</div>
                </div>
                <div className="server_manager_logs" ref={ref}>
                    {
                        logs.map((log, index) => {
                            return <div className="server_manager_log" key={'line_' + index}>{log}</div>;
                        })
                    }
                </div>
            </fieldset>
        </div>
    </>;
}
/* -------------------------------------------------------------------------------- *\
|                               PanelReportConfig
\* -------------------------------------------------------------------------------- */
// V2 parameters consumed by pdfgenerator/src/config.ts, grouped by type.
const V2_BOOL_KEYS = ['GALOP_SHOW_DISTVSFIRST', 'GALOP_SHOW_STRIDES', 'GALOP_SHOW_YARDS', 'GALOP_SHOW_MILLI', 'TROT_SHOW_DISTVSFIRST'];
const V2_NUMBER_KEYS = ['GALOP_SPEED_MIN', 'GALOP_SPEED_MAX', 'GALOP_STRIDE_MIN', 'GALOP_STRIDE_MAX', 'GALOP_LASTSECTION_COUNT', 'GALOP_CUSTOMER_LOGO_WIDTH', 'TROT_SPEED_MIN', 'TROT_SPEED_MAX', 'TROT_CUSTOMER_LOGO_WIDTH'];
const V2_COLOR_KEYS = ['GALOP_COLOR_TITLE1_BACK', 'GALOP_COLOR_TITLE1_TEXT', 'GALOP_COLOR_TITLE2_BACK', 'GALOP_COLOR_TITLE2_TEXT', 'TROT_COLOR_TITLE1_BACK', 'TROT_COLOR_TITLE1_TEXT', 'TROT_COLOR_TITLE2_BACK', 'TROT_COLOR_TITLE2_TEXT'];
const V2_TEXT_KEYS = ['TIMEZONE', 'GALOP_MAIL_DEST', 'GALOP_MAIL_COPY', 'GALOP_CUSTOMER_LOGO_PATH', 'TROT_MAIL_DEST', 'TROT_MAIL_COPY', 'TROT_CUSTOMER_LOGO_PATH', 'GALOP_MESSAGE', 'TROT_MESSAGE'];
const V2_SELECT_KEYS = ['GALOP_SPEED_UNIT', 'TROT_SPEED_UNIT', 'TROT_MODE'];
// Default values mirrored from pdfgenerator/src/config.ts (GalopConfig / TrotConfig / Config)
const V2_DEFAULTS = {
    TIMEZONE: 'Europe/Paris',
    GALOP_SPEED_MIN: '25',
    GALOP_SPEED_MAX: '85',
    GALOP_STRIDE_MIN: '3',
    GALOP_STRIDE_MAX: '9',
    GALOP_LASTSECTION_COUNT: '3',
    GALOP_SHOW_DISTVSFIRST: true,
    GALOP_SHOW_STRIDES: true,
    GALOP_SPEED_UNIT: 'kmh',
    GALOP_SHOW_YARDS: false,
    GALOP_SHOW_MILLI: false,
    GALOP_COLOR_TITLE1_BACK: '#D49C3D',
    GALOP_COLOR_TITLE1_TEXT: '#000000',
    GALOP_COLOR_TITLE2_BACK: '#282c34',
    GALOP_COLOR_TITLE2_TEXT: '#FFFFFF',
    GALOP_MAIL_DEST: '',
    GALOP_MAIL_COPY: '',
    GALOP_CUSTOMER_LOGO_PATH: '',
    GALOP_CUSTOMER_LOGO_WIDTH: '150',
    GALOP_MESSAGE: '',
    TROT_SPEED_MIN: '25',
    TROT_SPEED_MAX: '85',
    TROT_SHOW_DISTVSFIRST: true,
    TROT_COLOR_TITLE1_BACK: '#D49C3D',
    TROT_COLOR_TITLE1_TEXT: '#000000',
    TROT_COLOR_TITLE2_BACK: '#282c34',
    TROT_COLOR_TITLE2_TEXT: '#FFFFFF',
    TROT_MAIL_DEST: '',
    TROT_MAIL_COPY: '',
    TROT_MODE: 'French',
    TROT_SPEED_UNIT: 'kmh',
    TROT_CUSTOMER_LOGO_PATH: '',
    TROT_CUSTOMER_LOGO_WIDTH: '150',
    TROT_MESSAGE: '',
};

function PanelReportConfig()
{
    const { t } = useTranslation();
    const [form] = Form.useForm();
    const [logos, setLogos] = useState([]);
    const [logoPreview, setLogoPreview] = useState({}); // keyed by discipline prefix: GALOP / TROT
    const [modified, setModified] = useState(false);
    const reportMode = Form.useWatch('REPORT_MODE', form);

    useEffect(() =>
    {
        settings.getReportConfig().then(res =>
        {
            if (!res || !res.data) return;
            setLogos(res.data.logos || []);
            applyConfig(res.data.config || {});
        }).catch(() => message.error(t('common.error')));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // fill the form (and logo previews) from a config object { KEY: 'value' }
    const applyConfig = (c) =>
    {
        const fields = {
            REPORT_MODE: c.REPORT_MODE || "None",
            // V1
            REPORT_LOGO: c.REPORT_LOGO || undefined,
            MAILER_PDF_REPORT_TITLE_PREFIX: c.MAILER_PDF_REPORT_TITLE_PREFIX || '',
            MAILER_PDF_REPORT_TOS: c.MAILER_PDF_REPORT_TOS || '',
        };
        // V2: fall back to config.ts defaults when the stored value is empty/undefined
        [...V2_TEXT_KEYS, ...V2_NUMBER_KEYS, ...V2_COLOR_KEYS, ...V2_SELECT_KEYS].forEach(key => { fields[key] = (c[key] !== undefined && c[key] !== '') ? c[key] : V2_DEFAULTS[key]; });
        V2_BOOL_KEYS.forEach(key => { fields[key] = c[key] !== undefined && c[key] !== '' ? c[key] === 'true' : V2_DEFAULTS[key]; });
        form.setFieldsValue(fields);
        const previews = {};
        ['GALOP', 'TROT'].forEach(prefix =>
        {
            const logoPath = fields[prefix + '_CUSTOMER_LOGO_PATH'];
            if (logoPath) previews[prefix] = settings.getReportLogoV2Url(logoPath) + '?t=' + Date.now();
        });
        setLogoPreview(previews);
    };

    // build the config object { KEY: 'value' } from the form values
    const buildConfig = (values) =>
    {
        let data = { REPORT_MODE: values.REPORT_MODE || 'None' };
        if (values.REPORT_MODE === 'V1')
        {
            data = {
                ...data,
                REPORT_LOGO: values.REPORT_LOGO || '',
                MAILER_PDF_REPORT_TITLE_PREFIX: values.MAILER_PDF_REPORT_TITLE_PREFIX || '',
                MAILER_PDF_REPORT_TOS: values.MAILER_PDF_REPORT_TOS || '',
            };
        }
        else if (values.REPORT_MODE === 'V2')
        {
            V2_SELECT_KEYS.forEach(key => { data[key] = values[key] || V2_DEFAULTS[key]; });
            [...V2_TEXT_KEYS, ...V2_NUMBER_KEYS, ...V2_COLOR_KEYS].forEach(key => { data[key] = values[key] !== undefined && values[key] !== null ? String(values[key]) : ''; });
            V2_BOOL_KEYS.forEach(key => { data[key] = values[key] ? 'true' : 'false'; });
        }
        return data;
    };

    const onFinish = (values) =>
    {
        settings.saveReportConfig(buildConfig(values))
            .then(() => { setModified(false); message.success(t('common.save_success')); })
            .catch(() => message.error(t('common.error')));
    };

    const handleExport = () =>
    {
        const blob = new Blob([JSON.stringify(buildConfig(form.getFieldsValue()), null, 2)], { type: 'application/json' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = 'report_config.json';
        link.click();
        URL.revokeObjectURL(link.href);
    };

    const handleImport = (file) =>
    {
        file.text()
            .then(text =>
            {
                const c = JSON.parse(text);
                if (!c || typeof c !== 'object' || Array.isArray(c) || !['None', 'V1', 'V2'].includes(c.REPORT_MODE)) throw new Error('invalid');
                applyConfig(c);
                setModified(true);
            })
            .catch(() => message.error(t('common.InvalidFormatFile')));
        return false; // prevent antd default upload
    };

    const handleLogoUpload = (prefix, file) =>
    {
        settings.uploadReportLogoV2(file)
            .then(res =>
            {
                form.setFieldsValue({ [prefix + '_CUSTOMER_LOGO_PATH']: res.path });
                setLogoPreview(prev => ({ ...prev, [prefix]: settings.getReportLogoV2Url(res.path) + '?t=' + Date.now() }));
                setModified(true);
                message.success(t('common.save_success'));
            })
            .catch(() => message.error(t('common.error')));
        return false; // prevent antd default upload
    };

    const handleLogoDelete = (prefix) =>
    {
        form.setFieldsValue({ [prefix + '_CUSTOMER_LOGO_PATH']: '' });
        setLogoPreview(prev => ({ ...prev, [prefix]: '' }));
        setModified(true);
    };

    // customer logo (path + upload/delete + preview + width) and speed unit, shared by GALOP and TROT tabs
    const renderLogoItems = (prefix) => <>
        <Form.Item label={t('settings.report_config.customer_logo_label')}>
            <Form.Item name={prefix + '_CUSTOMER_LOGO_PATH'} noStyle>
                <Input readOnly placeholder={t('settings.report_config.no_image_placeholder')} />
            </Form.Item>
            <Upload showUploadList={false} accept="image/*" beforeUpload={(file) => handleLogoUpload(prefix, file)}>
                <Button icon={<UploadOutlined />} style={{ marginTop: 8 }}>{t('settings.report_config.upload_image_button')}</Button>
            </Upload>
            <Button danger disabled={!logoPreview[prefix]} icon={<DeleteOutlined />} onClick={() => handleLogoDelete(prefix)} style={{ marginTop: 8, marginLeft: 8 }}>{t('settings.report_config.delete_image_button')}</Button>
            {logoPreview[prefix] && <img src={logoPreview[prefix]} alt="customer logo" style={{ display: 'block', marginTop: 8, maxWidth: 200, maxHeight: 120, border: '1px solid #d9d9d9', padding: 4 }} />}
        </Form.Item>
        <Form.Item name={prefix + '_CUSTOMER_LOGO_WIDTH'} label={t('settings.report_config.customer_logo_width_label')}
            rules={[{ required: true }, { validator: (_, v) => Number(v) > 0 ? Promise.resolve() : Promise.reject(new Error(t('settings.report_config.must_be_positive_error'))) }]}>
            <Input type="number" min={1} />
        </Form.Item>
    </>;

    const renderSpeedUnitItem = (prefix) =>
        <Form.Item name={prefix + '_SPEED_UNIT'} label={t('settings.report_config.speed_unit_label')}>
            <Select>
                <Option key="kmh" value="kmh">km/h</Option>
                <Option key="mph" value="mph">mph</Option>
            </Select>
        </Form.Item>;

    return (
        <Form {...layout} form={form} name="report_config_form" className="common_form" onFinish={onFinish} onValuesChange={() => setModified(true)}>
            <div style={{ position: 'sticky', top: 0, zIndex: 10, background: '#fff', paddingTop: 8, borderBottom: '1px solid #f0f0f0' }}>
                <Form.Item name="REPORT_MODE" label={t('settings.report_config.report_type_label')} rules={[{ required: true }]}>
                    <Select>
                        <Option key="None" value="None">None</Option>
                        <Option key="V1" value="V1">V1</Option>
                        <Option key="V2" value="V2">V2</Option>
                    </Select>
                </Form.Item>

                <Form.Item {...tailLayout} className="form_toolbar" style={{ marginBottom: 8 }}>
                    <Button type="primary" htmlType="submit">{t('common.save')}</Button>
                    <Button icon={<DownloadOutlined />} onClick={handleExport} style={{ marginLeft: 64 }}>{t('common.export')}</Button>
                    <Upload showUploadList={false} accept=".json,application/json" beforeUpload={handleImport}>
                        <Button icon={<UploadOutlined />} style={{ marginLeft: 8 }}>{t('common.import')}</Button>
                    </Upload>
                    {modified && <div style={{ marginTop: 8, color: '#FF0000' }}><WarningOutlined style={{ marginRight: 6 }} />{t('settings.report_config.modified_message')}</div>}
                </Form.Item>
            </div>

            {reportMode === 'V1' && <>
                <Form.Item name="REPORT_LOGO" label={t('settings.report_config.logo_label')}>
                    <Select allowClear placeholder={t('settings.report_config.select_logo_placeholder')}>
                        {logos.map(logo => <Option key={logo} value={logo}>{logo}</Option>)}
                    </Select>
                </Form.Item>
                <Form.Item name="MAILER_PDF_REPORT_TITLE_PREFIX" label={t('settings.report_config.email_title_prefix_label')}>
                    <Input />
                </Form.Item>
                <Form.Item name="MAILER_PDF_REPORT_TOS" label={t('settings.report_config.email_recipients_label')} extra={t('settings.report_config.email_recipients_extra')}>
                    <Input.TextArea rows={3} />
                </Form.Item>
            </>}

            {reportMode === 'V2' && <>
                <fieldset>
                    <legend>{t('settings.report_config.general_legend')}</legend>
                    <Form.Item name="TIMEZONE" label={t('settings.report_config.timezone_label')} extra={t('settings.report_config.timezone_extra')}>
                        <Input />
                    </Form.Item>
                </fieldset>
                <Tabs defaultActiveKey="galop" items={[
                    {
                        key: 'galop',
                        label: t('settings.report_config.tab_galop'),
                        forceRender: true,
                        children: <>
                            <Form.Item name="GALOP_MAIL_DEST" label={t('settings.report_config.mail_recipients_label')} extra={t('settings.report_config.email_recipients_extra')}><Input /></Form.Item>
                            <Form.Item name="GALOP_MAIL_COPY" label={t('settings.report_config.mail_copy_label')} extra={t('settings.report_config.email_recipients_extra')}><Input /></Form.Item>
                            {renderLogoItems('GALOP')}
                            <Form.Item name="GALOP_SPEED_MIN" label={t('settings.report_config.speed_min_label')}><Input type="number" /></Form.Item>
                            <Form.Item name="GALOP_SPEED_MAX" label={t('settings.report_config.speed_max_label')}><Input type="number" /></Form.Item>
                            {renderSpeedUnitItem('GALOP')}
                            <Form.Item name="GALOP_STRIDE_MIN" label={t('settings.report_config.stride_min_label')}><Input type="number" /></Form.Item>
                            <Form.Item name="GALOP_STRIDE_MAX" label={t('settings.report_config.stride_max_label')}><Input type="number" /></Form.Item>
                            <Form.Item name="GALOP_LASTSECTION_COUNT" label={t('settings.report_config.last_sections_count_label')}><Input type="number" /></Form.Item>
                            <Form.Item name="GALOP_SHOW_DISTVSFIRST" label={t('settings.report_config.show_dist_vs_first_label')} valuePropName="checked"><Checkbox /></Form.Item>
                            <Form.Item name="GALOP_SHOW_STRIDES" label={t('settings.report_config.show_strides_label')} valuePropName="checked"><Checkbox /></Form.Item>
                            <Form.Item name="GALOP_SHOW_YARDS" label={t('settings.report_config.show_yards_label')} valuePropName="checked"><Checkbox /></Form.Item>
                            <Form.Item name="GALOP_SHOW_MILLI" label={t('settings.report_config.show_milliseconds_label')} valuePropName="checked"><Checkbox /></Form.Item>
                            <Form.Item name="GALOP_MESSAGE" label={t('settings.report_config.message_label')}><Input /></Form.Item>
                            <Form.Item name="GALOP_COLOR_TITLE1_BACK" label={t('settings.report_config.title1_background_label')}><Input type="color" /></Form.Item>
                            <Form.Item name="GALOP_COLOR_TITLE1_TEXT" label={t('settings.report_config.title1_text_label')}><Input type="color" /></Form.Item>
                            <Form.Item name="GALOP_COLOR_TITLE2_BACK" label={t('settings.report_config.title2_background_label')}><Input type="color" /></Form.Item>
                            <Form.Item name="GALOP_COLOR_TITLE2_TEXT" label={t('settings.report_config.title2_text_label')}><Input type="color" /></Form.Item>
                        </>
                    },
                    {
                        key: 'trot',
                        label: t('settings.report_config.tab_trot'),
                        forceRender: true,
                        children: <>
                            <Form.Item name="TROT_MAIL_DEST" label={t('settings.report_config.mail_recipients_label')} extra={t('settings.report_config.email_recipients_extra')}><Input /></Form.Item>
                            <Form.Item name="TROT_MAIL_COPY" label={t('settings.report_config.mail_copy_label')} extra={t('settings.report_config.email_recipients_extra')}><Input /></Form.Item>
                            <Form.Item name="TROT_MODE" label={t('settings.report_config.trot_mode_label')}>
                                <Select>
                                    <Option key="French" value="French">French</Option>
                                    <Option key="Rwwa" value="Rwwa">Rwwa</Option>
                                </Select>
                            </Form.Item>
                            {renderLogoItems('TROT')}
                            <Form.Item name="TROT_SPEED_MIN" label={t('settings.report_config.speed_min_label')}><Input type="number" /></Form.Item>
                            <Form.Item name="TROT_SPEED_MAX" label={t('settings.report_config.speed_max_label')}><Input type="number" /></Form.Item>
                            {renderSpeedUnitItem('TROT')}
                            <Form.Item name="TROT_SHOW_DISTVSFIRST" label={t('settings.report_config.show_dist_vs_first_label')} valuePropName="checked"><Checkbox /></Form.Item>
                            <Form.Item name="TROT_MESSAGE" label={t('settings.report_config.message_label')}><Input /></Form.Item>
                            <Form.Item name="TROT_COLOR_TITLE1_BACK" label={t('settings.report_config.title1_background_label')}><Input type="color" /></Form.Item>
                            <Form.Item name="TROT_COLOR_TITLE1_TEXT" label={t('settings.report_config.title1_text_label')}><Input type="color" /></Form.Item>
                            <Form.Item name="TROT_COLOR_TITLE2_BACK" label={t('settings.report_config.title2_background_label')}><Input type="color" /></Form.Item>
                            <Form.Item name="TROT_COLOR_TITLE2_TEXT" label={t('settings.report_config.title2_text_label')}><Input type="color" /></Form.Item>
                        </>
                    },
                ]} />
            </>}
        </Form>
    );
}

/* -------------------------------------------------------------------------------- *\
|                               PanelHpv2Servers
\* -------------------------------------------------------------------------------- */
const EMPTY_SERVER = { name: '', url: '', login: '', password: '', isSsl: false };

function PanelHpv2Servers()
{
    const {t} = useTranslation();
    const [list, setList] = useState([]);
    const [modalOpen, setModalOpen] = useState(false);
    const [editing, setEditing] = useState(null); // null = create, object = edit
    const [form] = Form.useForm();

    const load = () =>
    {
        positionServer.getAll().then(r => setList(r?.data || []));
    };

    useEffect(() => { load(); }, []);

    const openCreate = () =>
    {
        setEditing(null);
        form.setFieldsValue(EMPTY_SERVER);
        setModalOpen(true);
    };

    const openEdit = (item) =>
    {
        setEditing(item);
        form.setFieldsValue({ name: item.name, url: item.url, login: item.login, password: '', isSsl: item.isSsl });
        setModalOpen(true);
    };

    const handleDelete = (item) =>
    {
        Modal.confirm({
            title: t('settings.hpv2_servers.delete_confirm_title', { name: item.name }),
            onOk: () => positionServer.del(item.uuid)
                .then(() => { message.success(t('settings.hpv2_servers.deleted_success')); load(); })
                .catch(() => message.error(t('common.error')))
        });
    };

    const handleSave = () =>
    {
        form.validateFields().then(values =>
        {
            const data = { ...values };
            if (editing && !data.password) delete data.password;
            const call = editing
                ? positionServer.update({ ...data, uuid: editing.uuid })
                : positionServer.save(data);
            call
                .then(() => { message.success(t('common.save_success')); setModalOpen(false); load(); })
                .catch(() => message.error(t('common.error')));
        });
    };

    return (
        <>
            <div style={{ marginBottom: 12 }}>
                <Button icon={<PlusOutlined />} type="primary" onClick={openCreate} style={{ marginLeft: 10 }}>{t('settings.hpv2_servers.add_button')}</Button>
            </div>
            <table className="hpv2-servers-table" style={{ borderCollapse: 'collapse', maxWidth: 650, marginLeft: 10 }}>
                <thead>
                    <tr>
                        <th style={{ border: '1px solid #d9d9d9', padding: '6px 12px' }}>{t('settings.hpv2_servers.col_name')}</th>
                        <th style={{ border: '1px solid #d9d9d9', padding: '6px 12px' }}>{t('settings.hpv2_servers.col_url')}</th>
                        <th style={{ border: '1px solid #d9d9d9', padding: '6px 12px' }}>{t('settings.hpv2_servers.col_ssl')}</th>
                        <th style={{ border: '1px solid #d9d9d9', padding: '6px 12px' }}>{t('settings.hpv2_servers.col_login')}</th>
                        <th style={{ border: '1px solid #d9d9d9', padding: '6px 12px' }}>{t('settings.hpv2_servers.col_password')}</th>
                        <th style={{ border: '1px solid #d9d9d9', padding: '6px 12px' }}></th>
                    </tr>
                </thead>
                <tbody>
                    {list.map(item => (
                        <tr key={item.uuid}
                            onMouseEnter={e => e.currentTarget.style.background = '#fafafa'}
                            onMouseLeave={e => e.currentTarget.style.background = ''}
                        >
                            <td style={{ border: '1px solid #d9d9d9', padding: '6px 12px', verticalAlign: 'middle' }}>{item.name}</td>
                            <td style={{ border: '1px solid #d9d9d9', padding: '6px 12px', verticalAlign: 'middle' }}>{item.url}</td>
                            <td style={{ border: '1px solid #d9d9d9', padding: '6px 12px', verticalAlign: 'middle' }}>{item.isSsl ? t('settings.hpv2_servers.yes') : t('settings.hpv2_servers.no')}</td>
                            <td style={{ border: '1px solid #d9d9d9', padding: '6px 12px', verticalAlign: 'middle' }}>{item.login}</td>
                            <td style={{ border: '1px solid #d9d9d9', padding: '6px 12px', verticalAlign: 'middle' }}>{item.password ? '••••••••' : ''}</td>
                            <td style={{ border: '1px solid #d9d9d9', padding: '6px 12px', verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                                <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(item)} style={{ marginRight: 6, height: 24 }} />
                                <Button size="small" danger icon={<DeleteOutlined />} onClick={() => handleDelete(item)} style={{ height: 24 }} />
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>

            <Modal
                title={editing ? t('settings.hpv2_servers.edit_modal_title') : t('settings.hpv2_servers.add_modal_title')}
                open={modalOpen}
                onOk={handleSave}
                onCancel={() => setModalOpen(false)}
                maskClosable={false}
                okText={t('common.save')}
            >
                <Form form={form} layout="vertical">
                    <Form.Item name="name" label={t('settings.hpv2_servers.col_name')} rules={[{ required: true }]}>
                        <Input />
                    </Form.Item>
                    <Form.Item name="url" label={t('settings.hpv2_servers.col_url')} rules={[{ required: true }]}>
                        <Input />
                    </Form.Item>
                    <Form.Item name="login" label={t('settings.hpv2_servers.col_login')}>
                        <Input />
                    </Form.Item>
                    <Form.Item name="password" label={t('settings.hpv2_servers.col_password')}>
                        <Input.Password placeholder={editing ? t('settings.hpv2_servers.password_placeholder') : ''} />
                    </Form.Item>
                    <Form.Item name="isSsl" label={t('settings.hpv2_servers.col_ssl')} valuePropName="checked">
                        <Checkbox />
                    </Form.Item>
                </Form>
            </Modal>
        </>
    );
}

/* -------------------------------------------------------------------------------- *\
|                               PrivateSettings
\* -------------------------------------------------------------------------------- */
export default function PrivateSettings() {
    const {t} = useTranslation();
    const [formHpv2Selection] = Form.useForm();
    const [formHpv2Settings] = Form.useForm();
    const [hpv2List, setHpv2List] = useState([]);
    const [user, setUser] = useState({});
    const [hpv2Selected, setHpv2Selected] = useState({});

    // loadHpv2 ------------------------------------------------------
    const loadHpv2 = async () => {
        positionServer.getAll().then(positionsServers => {
            setHpv2List(positionsServers.data);
            const currentSelectedServer = positionsServers.data.find(server => server.isSelected === true);
            if (currentSelectedServer) {
                setHpv2Selected(currentSelectedServer);
            }
        });
    }

    const loadHpv2Settings = async () => {
        positionServer.getHpv2Settings().then(hpv2Settings => {
            if (hpv2Settings && hpv2Settings.data) updateHpv2SettingsFields(hpv2Settings.data);
        });
    }

    const updateHpv2SettingsFields = (hpv2Settings) => {
        formHpv2Settings.setFieldsValue({hpv2_mode: `${hpv2Settings.mode}`});
        formHpv2Settings.setFieldsValue({hpv2_tracker_height: hpv2Settings.height_sensor});
        formHpv2Settings.setFieldsValue({hpv2_channel: hpv2Settings.channel});
        formHpv2Settings.setFieldsValue({hpv2_mcc_mnc: `${hpv2Settings.mccMnc}`});
        formHpv2Settings.setFieldsValue({hpv2_network_technology: `${hpv2Settings.networkTechnology}`});
    }

    // useEffect ------------------------------------------------------
    useEffect(() => {
        setUser(JSON.parse(localStorage.getItem('user')));
        loadHpv2();
        loadHpv2Settings();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // render ---------------------------------------------------------
    const tabItems = [
        {
            key: "2",
            label: t('settings.hpv2_settings.title') + ` (${hpv2Selected.name})`,
            children: <PanelHPv2Configuration formHpv2Settings={formHpv2Settings} />
        },
        ...(user && user.roles && user.roles.includes("admin") ? [{
            key: "1",
            label: t('settings.hpv2_selection.title'),
            children: <PanelHPv2Selection hpv2List={hpv2List} selected={hpv2Selected} formHpv2Selection={formHpv2Selection} />
        }] : []),
        {
            key: "4",
            label: t('settings.server_manager.title'),
            children: <PanelServerManager/>
        },
        ...(user && user.roles && user.roles.includes("admin") ? [{
            key: "6",
            label: t('settings.report_config.title'),
            children: <PanelReportConfig/>
        }] : []),
        ...(user && user.roles && user.roles.includes("admin") ? [{
            key: "5",
            label: t('settings.hpv2_servers.title'),
            children: <PanelHpv2Servers/>
        }] : [])
    ];

    return (
        <div className="private_page_wrapper private-settings">
            <div className="header">
                <span className="title">{t('Menu.Settings')}</span>
            </div>
            <Tabs defaultActiveKey="2" items={tabItems} />
        </div>
    )

}
